/*
 * Copyright (c) 2020 General Electric Company. All rights reserved.
 *
 * The copyright to the computer software herein is the property of
 * General Electric Company. The software may be used and/or copied only
 * with the written permission of General Electric Company or in accordance
 * with the terms and conditions stipulated in the agreement/contract
 * under which the software has been supplied.
 */

import { Injectable, OnDestroy } from '@angular/core';
import { Logger } from '@core/lib/services';
import * as _ from 'underscore';
import * as lodash from 'lodash';
import { Subscription } from 'rxjs';
import { EventMessage } from '@app/common/event/event-message';
import { Events } from '@app/common/event/events';
import { EventType } from '@app/common/event/event-type';
import { ProxyDataService } from '@app/core/http/proxy-data.service';
import { CurrentExamContextService } from '@app/common/current-exam-context.service';
import { ArchiveAdoptedService } from '@app/common/services/bring-online/archive-adopted.service';
import { EventWorkItem } from '@app/data-model/event-work-item';
import { appConstants } from '@app/common/app-constants';
import { MessageService } from '@core/lib/services/message.service';
import { UtilService } from '@app/util/util.service';
import { AppConfigService } from '@app/common/app-config.service';
import { TabContextService } from '@app/ui/client-cache/tab-context.service';
import { AuditService } from '../audit.service';

const log = new Logger('EventService');

@Injectable({
  providedIn: 'root'
})
export class EventService implements OnDestroy {
  // For Eventing
  private beforeEventHandlers = new Map<string, ((e: any, t) => void)[]>();
  private afterSubscriptions: Subscription[] = [];

  private nawInstituteCheckCallback;

  // For Messaging between
  constructor(
    private dataService: ProxyDataService,
    private currentExamContextService: CurrentExamContextService,
    private archiveAdoptedService: ArchiveAdoptedService,
    private messageService: MessageService,
    private appConfigService: AppConfigService,
    private utilService: UtilService,
    private tabContextService: TabContextService,
    private auditService: AuditService
  ) {
    this.beforeEvents();
  }

  public registerNawInstituteCheckCallback(callback: (workItem: any) => void) {
    this.nawInstituteCheckCallback = callback;
  }

  sendEvent(eventMessage: EventMessage) {
    return this.processEvent(eventMessage);
  }

  beforeEvent(eventName: string, method: (e, t) => void, order?: number) {
    if (Events[eventName]) {
      if (!this.beforeEventHandlers.has(eventName)) {
        this.beforeEventHandlers.set(eventName, []);
      }
      if (_.isUndefined(order)) {
        this.beforeEventHandlers.get(eventName).push(method);
      } else {
        this.beforeEventHandlers.get(eventName).splice(order, 0, method);
      }
    } else {
      log.error(eventName + ' - is not a valid event, Please the event name');
    }
  }

  afterEvent(eventName: string, callback, eventType?: string): Subscription {
    if (EventService.isValidEventType(eventType)) {
      const topic = eventName + '.after' + (EventService.isValidEventType(eventType) ? '.' + eventType : '');
      return this.subscribeAfterEvent(topic, callback);
    } else if (Events[eventName].type === EventType.LOCAL) {
      return this.subscribeAfterEvent(eventName + '.after.' + EventType.LOCAL, callback);
    } else {
      let topic = eventName + '.after.' + EventType.REMOTE;
      this.subscribeAfterEvent(topic, callback);
      topic = eventName + '.after.' + EventType.LOCAL;
      return this.subscribeAfterEvent(topic, callback);
    }
  }

  static isValidEventType(eventType: string) {
    return eventType === EventType.LOCAL || eventType === EventType.REMOTE;
  }

  private subscribeAfterEvent(topic: string, callback?): Subscription {
    const subscriber: Subscription = this.messageService.on(topic).subscribe((data: any) => {
      if (callback) {
        if (topic.indexOf(EventType.REMOTE) > -1) {
          callback({ id: data.workitemId }, data);
        } else if (data && data.target && data.state) {
          callback(data.target, data.state);
        } else if (data) {
          callback(data);
        } else {
          callback();
        }
      }
    });
    this.afterSubscriptions.push(subscriber);
    return subscriber;
  }

  private handleCloseDiscardError(workItem, error) {
    if (!UtilService.isRemoteExam(workItem)) {
      if (error === 'ERR_NO_RECORD_FOUND') {
        this.messageService.broadcast('closeDiscardForExamNotExists', {
          id: workItem.id
        });
      } else {
        log.error(
          'Requested Procedure ID: ' + workItem.id + ', closediscard action failed, Error - ' + JSON.stringify(error)
        );
      }
    }
  }

  private processEvent(eventMessage: EventMessage): Promise<any> {
    return new Promise((resolve, reject) => {
      if (eventMessage) {
        if (Events[eventMessage.event.name]) {
          try {
            this.preEventActions(eventMessage);
            Promise.all(this.executeBeforeHandlers(eventMessage, this.beforeEventHandlers.get(eventMessage.event.name)))
              .then(() => {
                if (eventMessage.event.type === EventType.REMOTE) {
                  const requestBody = this.getData(eventMessage);
                  const targetId = requestBody.targetId;
                  delete requestBody.targetId;
                  const promise = this.dataService.update(appConstants.appURLs.workItemState, targetId, requestBody);
                  promise &&
                    promise.then(
                      state => {
                        this.sendAfterEventMessage(eventMessage, state);
                        this.postEventActions(eventMessage, state);
                        resolve(state);
                      },
                      error => {
                        this.postErrorEventActions(eventMessage, error);
                        reject(`updating work item state ${JSON.stringify(error)}`);
                      }
                    );
                } else {
                  this.postEventActions(eventMessage, null);
                  this.sendAfterEventMessage(eventMessage);
                  resolve({ eventMessage });
                }
              })
              .catch(reason => {
                reject(`executing before actions => ${JSON.stringify(reason)}`);
              });
          } catch (ex) {
            reject(`unexpected error occurred - ${JSON.stringify(ex)}`);
          }
        } else {
          reject(`${eventMessage.event.name} event is not available, Please check event name`);
        }
      } else {
        reject('Event input is invalid');
      }
    });
  }

  private prepareRequestData(eventMessage, targetId, requestBody): any {
    let data;
    if (eventMessage.workItem && targetId !== '') {
      data = { stateActions: requestBody };
    } else {
      data = {
        id: targetId,
        actionCode: eventMessage.event.value,
        actorName: this.appConfigService.getUserId(),
        ownerName: (!!eventMessage.params && eventMessage.params.userName) || this.appConfigService.getUserId(),
        reportStatus: requestBody.reportStatus || '',
        associationList: requestBody.associationList || null,
        externalLockInsert: requestBody.externalLockInsert || false,
        updateNextApprover: requestBody.updateNextApprover || false,
        resetExamStatus: requestBody.resetExamStatus || false,
        keepAssignment: requestBody.keepAssignment || false,
        takeOwnership: requestBody.takeOwnership || false
      };
      if (requestBody.ids && requestBody.ids.length > 0) data.ids = requestBody.ids;
    }
    data.targetId = targetId;
    return data;
  }

  private getData(eventMessage: EventMessage) {
    let requestData;
    if (eventMessage.event.type === EventType.REMOTE) {
      if (eventMessage.workItem) {
        const tabsMap = [];
        const userName = this.appConfigService.getUserId();

        eventMessage.workItem.forEach(target => {
          if (target.keys.ids === undefined && !UtilService.isRemoteExam(target)) {
            if (
              eventMessage.workItem.length > 1 &&
              (eventMessage.event.value === 'READ_CLOSE_DISCARD' || eventMessage.event.value === 'UNASSIGN')
            ) {
              tabsMap.push({
                ids: target.ids,
                actionCode: eventMessage.event.value,
                actorName: userName,
                ownerName: (!!eventMessage.params && eventMessage.params.userName) || userName,
                reportStatus: target.reportStatus || '',
                associationList: target.associationList || null,
                externalLockInsert: target.externalLockInsert || false,
                updateNextApprover: target.updateNextApprover || false,
                resetExamStatus: target.resetExamStatus || false,
                keepAssignment: target.keepAssignment || false,
                workItemId: target.id,
                takeOwnership: target.takeOwnership || false
              });
            } else {
              requestData = this.prepareRequestData(eventMessage, target.id, [
                {
                  id: target.id,
                  actionCode: eventMessage.event.value,
                  actorName: userName,
                  ownerName: (!!eventMessage.params && eventMessage.params.userName) || userName,
                  reportStatus: target.reportStatus || '',
                  associationList: target.associationList || null,
                  externalLockInsert: target.externalLockInsert || false,
                  updateNextApprover: target.updateNextApprover || false,
                  resetExamStatus: target.resetExamStatus || false,
                  keepAssignment: target.keepAssignment || false,
                  takeOwnership: target.takeOwnership || false
                }
              ]);
            }
          } else if (!UtilService.isRemoteExam(target)) {
            requestData = this.prepareRequestData(eventMessage, '', {
              id: '',
              reportStatus: '',
              associationList: '',
              externalLockInsert: '',
              updateNextApprover: '',
              ids: target.keys.ids
            });
          }
        });
        if (tabsMap.length >= 1) {
          requestData = this.prepareRequestData(eventMessage, -1, tabsMap);
        }
      }
    }
    if (!requestData) {
      requestData = this.prepareRequestData(eventMessage, '', {});
    }
    if (eventMessage?.params?.removedAssociationList) {
      requestData.stateActions[0].removedAssociationList = eventMessage.params.removedAssociationList.map(
        assocInfo => assocInfo.examId
      );
    }
    if (eventMessage?.params?.addedAssociationList) {
      requestData.stateActions[0].associationList = eventMessage.params.addedAssociationList.map(
        assocInfo => assocInfo.examId
      );
    }

    return requestData;
  }

  private sendAfterEventMessage(eventMessage: EventMessage, states?: any) {
    if (eventMessage.workItem instanceof Array) {
      for (const workItem of eventMessage.workItem) {
        this.afterEventMessageBroadcast(eventMessage, workItem, states);
      }
    }
  }

  private afterEventMessageBroadcast(eventMessage: EventMessage, workItem: EventWorkItem, states: any) {
    const topic = eventMessage.event.name + '.after.' + EventType.LOCAL;
    let itemStateSpecificToTarget;
    if (eventMessage.event.type === EventType.REMOTE) {
      if (!!workItem && workItem.associationList && workItem.associationList.length > 0 && workItem.id === -1) {
        itemStateSpecificToTarget = states[0];
      } else {
        itemStateSpecificToTarget = _.findWhere(states, { workitemId: workItem.id });
      }

      if (
        eventMessage.event.name === Events.selectCoReader.name ||
        eventMessage.event.name === Events.rejectReport.name
      ) {
        this.broadcastAssignmentChangeOnNawEvent(states, workItem);
      }

      if (eventMessage.event.name === Events.unassignAll.name || eventMessage.event.name === Events.assignAll.name) {
        this.messageService.broadcast(topic, {
          target: workItem,
          state: states
        });
      } else {
        if (states instanceof Array && states.length > 1) {
          states.forEach(state => {
            this.messageService.broadcast(topic, {
              target: workItem,
              state: UtilService.isRemoteExam(workItem) ? {} : state
            });
          });
        } else {
          this.messageService.broadcast(topic, {
            target: workItem,
            state: UtilService.isRemoteExam(workItem) ? {} : itemStateSpecificToTarget
          });
        }
      }
    } else {
      this.messageService.broadcast(topic, workItem);
    }
  }

  private broadcastAssignmentChangeOnNawEvent(states: any, workItem: EventWorkItem) {
    if (states instanceof Array && states.length === 1) {
      states = states[0];
    }

    if (states instanceof Array) {
      states.forEach(state => {
        const topic = state.ownership.assignedByUser
          ? Events.assign.name + '.after.' + EventType.LOCAL
          : Events.unassign.name + '.after.' + EventType.LOCAL;
        this.messageService.broadcast(topic, {
          target: workItem,
          state: UtilService.isRemoteExam(workItem) ? {} : state
        });
      });
    } else {
      const topic = states.ownership.assignedByUser
        ? Events.assign.name + '.after.' + EventType.LOCAL
        : Events.unassign.name + '.after.' + EventType.LOCAL;
      this.messageService.broadcast(topic, {
        target: workItem,
        state: UtilService.isRemoteExam(workItem) ? {} : states
      });
    }
  }

  private executeBeforeHandlers(eventMessage: EventMessage, callbacks: ((l: any, target) => void)[]): Promise<any>[] {
    log.info('executing event before handlers for ' + eventMessage.event.name);
    const beforePromises = [];
    if (!_.isUndefined(callbacks)) {
      if (eventMessage.workItem instanceof Array) {
        for (const workItem of eventMessage.workItem) {
          this.executeBeforePromises(callbacks, beforePromises, workItem, eventMessage.event.name);
        }
      } else {
        this.executeBeforePromises(callbacks, beforePromises, eventMessage.workItem, eventMessage.event.name);
      }
    }
    return beforePromises;
  }

  private executeBeforePromises(
    callbacks: ((l: any, target: any) => void)[],
    beforePromises: any[],
    eventWorkItem: EventWorkItem,
    eventName: string
  ) {
    for (const callback of callbacks) {
      beforePromises.push(
        new Promise((resolve, reject) => {
          const promiseResponseTimeout = setTimeout(() => {
            reject(eventName + ' - event timeout occurred');
            clearTimeout(promiseResponseTimeout);
          }, 2000);
          callback(
            {
              done: () => {
                clearTimeout(promiseResponseTimeout);
                resolve(undefined);
              },
              halt: (reason?: any) => {
                clearTimeout(promiseResponseTimeout);
                reject(reason);
              }
            },
            eventWorkItem
          );
        })
      );
    }
  }

  private updateCurrentExamContext(activeWorkItem) {
    return this.currentExamContextService.setContext(activeWorkItem);
  }

  private getCacheData(id) {
    const cachedAssociableExams = this.tabContextService.getAssociableExamsState(id);
    if (cachedAssociableExams) {
      return _.values(cachedAssociableExams);
    }
  }

  getAssociatedIds(id, examContext?) {
    const values = this.getCacheData(id);
    if (values && values.length > 0) {
      const associationList = [];
      if (examContext && examContext === 'preview') {
        _.each(values, exam => {
          if (exam.isAssociated === 'Y' && exam.state && !exam.state.isActivatedBySelf) {
            associationList.push(exam.requestedProcedureId);
          }
        });
      } else {
        _.each(values, exam => {
          if (exam.isAssociated === 'Y') {
            associationList.push(exam.requestedProcedureId);
          }
        });
      }
      if (associationList.length > 0) {
        return associationList;
      }
    }
  }

  private clearCurrentExamContext(activeWorkItem) {
    this.currentExamContextService.resetContext(activeWorkItem);
  }

  private processWorkItem(data, unassignFlag) {
    const workItem = this.getWorkItem(data, Events.closediscard.name);
    if (workItem) {
      const examContext = this.utilService.getTabContext(workItem.id);
      workItem.associationList = this.getAssociatedIds(workItem.id, examContext);
      workItem.updateNextApprover = !!workItem.keys.state && workItem.keys.state.isAssigned && !unassignFlag;
      this.clearCurrentExamContext(workItem);
    }
    return workItem;
  }

  private preEventActions(eventMessage): boolean {
    const actionName = eventMessage.event.name;
    const workItem = eventMessage.workItem[0];

    const params = _.extend({}, eventMessage.params);
    if (actionName === Events.assign.name) {
      eventMessage.workItem = this.getWorkItem(workItem, Events.assign.name);
    } else if (actionName === Events.unassign.name) {
      const result = [];
      const workItems = eventMessage.workItem;
      for (let i = 0; i < workItems.length; i++) {
        const workItemFromList = this.getWorkItem(workItems[i], Events.unassign.name);
        result.push(workItemFromList);
      }
      eventMessage.workItem = result;
    } else if (actionName === Events.unassignAll.name) {
      eventMessage.workItem = this.getWorkItem(
        {
          id: '-1',
          params: { ids: eventMessage.workItem }
        },
        Events.unassignAll.name
      );
    } else if (actionName === Events.assignAll.name) {
      eventMessage.workItem = this.getWorkItem(
        {
          id: '-1',
          params: { ids: eventMessage.workItem }
        },
        Events.assignAll.name
      );
    } else if (actionName === Events.preview.name) {
      const item = this.getWorkItem(workItem, Events.preview.name);
      if (params.source && params.source === 'patient-history') {
        item.keys.source = params.source;
        item.keys.comparisonAction = 'createOnly';
      } else if (!this.updateCurrentExamContext(item)) {
        return false;
      }
      // extract audit Info from work item
      const auditData = this.auditService.getExamAuditInfo(item);
      // when preview  an exam we need to log the presence of AI finding in the comment field
      // aiFinding exist only if the aiFinding value is greater than zero
      if (auditData && item.keys.aiFinding > 0) {
        log.debug('adding comment field for the audit ', auditData);
        log.debug('Exam ' + item.id + ' AI Finding present');
        auditData['comment'] = 'AI Finding present ';
      }
      eventMessage.params = { examAudit: auditData };
      eventMessage.workItem = item;
    } else if (actionName === Events.launchreadonly.name) {
      const item = this.getWorkItem(workItem, Events.launchreadonly.name);
      if (params.source && (params.source === 'diagnostic-hub' || params.source === 'patient-history')) {
        item.keys.studyClickTime = new Date().getTime();
        item.keys.disableAutoLaunchViewer = false;
        item.keys.source = params.source;
      }

      const auditData = this.auditService.getExamAuditInfo(item);
      // when launch  an exam we need to log the presence of AI finding in the comment field
      // aiFinding exist only if the aiFinding value is greater than zero
      if (auditData && item.keys.aiFinding > 0) {
        log.debug('adding comment field for the audit ', auditData);
        log.debug('Exam ' + item.id + ' AI Finding present');
        auditData['comment'] = 'AI Finding present ';
      }

      this.updateCurrentExamContext(item);
      if (params && params.launchOfflinePopup === true) {
        this.disableAutoLaunchViewerForArchiveAdopted(item);
        this.archiveAdoptedService.openArchivedWorkItemPopup(event, item.keys, item.id);
      }
      eventMessage.params = { examAudit: auditData };
      eventMessage.workItem = item;
    } else if (actionName === Events.read.name) {
      const associationList = params.associationList;
      let item = this.getWorkItem(workItem, Events.read.name);
      if (item) {
        if (params?.source !== 'desktop-integration') {
          this.updateCurrentExamContext(item);
        }
        if (params.source) {
          if (params.source === 'diagnostic-hub') {
            item.keys.studyClickTime = new Date().getTime();
          }
          item.keys.source = params.source;
          if (associationList) {
            item.associationList = associationList;
          }
        }
        item = this.updateExternalLockInsert(item);
        this.disableAutoLaunchViewerForArchiveAdopted(item);

        this.nawInstituteCheckCallback && this.nawInstituteCheckCallback(item);

        eventMessage.workItem = item;
      }
    } else if (actionName === Events.launchDictation.name) {
      const item = this.getWorkItem(workItem, Events.launchDictation.name);
      this.updateCurrentExamContext(item);
      // extract audit Info from work item
      const auditData = this.auditService.getExamAuditInfo(item);
      // when launch  an exam we need to log the presence of AI finding in the comment field
      // aiFinding exist only if the aiFinding value is greater than zero
      if (auditData && item.keys.aiFinding > 0) {
        log.debug('adding comment field for the audit ', auditData);
        log.debug('Exam ' + item.id + ' AI Finding present');
        auditData['comment'] = 'AI Finding present';
      }
      eventMessage.params = { examAudit: auditData };
      if (item && item.keys) {
        item.keys.studyClickTime = new Date().getTime();
      }
      eventMessage.workItem = item;
    } else if (actionName === Events.externalAccessTab.name) {
      const item = this.getWorkItem(workItem, Events.externalAccessTab.name);
      if (!this.updateCurrentExamContext(item)) {
        return false;
      }
      eventMessage.workItem = item;
    } else if (actionName === Events.takeControl.name) {
      const item = this.getWorkItem(workItem, Events.takeControl.name);
      this.updateCurrentExamContext(item);
      eventMessage.workItem = this.updateExternalLockInsert(item);
    } else if (actionName === Events.previewReport.name) {
      const item = this.getWorkItem(workItem, Events.previewReport.name);
      if (params.source && params.source === 'diagnostic-hub') {
        item.keys.source = params.source;
      }
      eventMessage.workItem = item;
    } else if (actionName === Events.closediscard.name) {
      const result = [];
      const workItems = eventMessage.workItem;
      for (let j = 0; j < workItems.length; j++) {
        const unassignFlag = _.isUndefined(workItems[j].isRelease)
          ? eventMessage.params && eventMessage.params.unassignFlag
          : workItems[j].isRelease;
        const workItem = this.processWorkItem(workItems[j], unassignFlag);
        result.push(workItem);
      }
      eventMessage.workItem = result;
    } else if (actionName === Events.close.name) {
      eventMessage.workItem = this.getWorkItem(workItem, Events.close.name);
    } else if (actionName === Events.clear.name) {
      const item = this.getWorkItem(workItem, Events.clear.name);
      this.clearCurrentExamContext(item);
      eventMessage.workItem = item;
    } else if (actionName === Events.closeviewer.name) {
      const item = this.getWorkItem(workItem, Events.closeviewer.name);
      this.clearCurrentExamContext(item);
      eventMessage.workItem = item;
    } else if (actionName === Events.releaseLock.name) {
    } else if (actionName === Events.closeExternalTab.name) {
      eventMessage.workItem = this.getWorkItem(workItem, Events.closeExternalTab.name);
    }
    return true;
  }

  private mergeComments(auditData, workItem, actionName) {
    if (
      auditData &&
      [Events.preview.name, Events.launchreadonly.name, Events.launchDictation.name].includes(actionName)
    ) {
      const concatComment = [auditData.comment, workItem.keys.eventComment].join(' ').trim();
      !!concatComment && (auditData.comment = concatComment);

      delete workItem.keys.eventComment;
    }
  }

  private postEventActions(eventMessage, state) {
    let workItem = eventMessage.workItem[0];
    const actionName = eventMessage.event.name;
    const auditData = !!eventMessage.params && eventMessage.params.examAudit;
    this.mergeComments(auditData, workItem, actionName);

    if (actionName === Events.assign.name) {
      this.auditService.saveAuditAction(
        this.auditService.getActionCode().Create,
        this.auditService.getActionType().Assign,
        this.auditService.getEventOutcome().Success,
        auditData
      );
    } else if (actionName === Events.unassign.name) {
      this.auditService.saveAuditAction(
        this.auditService.getActionCode().Delete,
        this.auditService.getActionType().UnAssign,
        this.auditService.getEventOutcome().Success,
        auditData
      );
    } else if (actionName === Events.selectCoReader.name) {
      this.auditNawAction(auditData, this.auditService.getActionType().NativeAcademicSelectCoReader);
      this.auditNawActionForSecondary(
        auditData,
        this.auditService.getActionType().NativeAcademicSelectCoReader,
        eventMessage
      );
    } else if (actionName === Events.rejectReport.name) {
      this.auditNawAction(auditData, this.auditService.getActionType().NativeAcademicWorkflowReject);
      this.auditNawActionForSecondary(
        auditData,
        this.auditService.getActionType().NativeAcademicWorkflowReject,
        eventMessage
      );
    } else if (actionName === Events.unassignAll.name) {
      log.info('unassignAll action executed successfully for Requested Procedure ID(s): ' + state);
    } else if (actionName === Events.assignAll.name) {
      if (auditData && eventMessage.params.userName) {
        auditData.ownerName = eventMessage.params.userName;
      }
      this.auditService.saveAuditAction(
        this.auditService.getActionCode().Update,
        this.auditService.getActionType().Assign,
        this.auditService.getEventOutcome().Success,
        auditData
      );
    } else if (actionName === Events.preview.name) {
      this.auditService.saveAuditAction(
        this.auditService.getActionCode().Read,
        this.auditService.getActionType().Preview,
        this.auditService.getEventOutcome().Success,
        auditData
      );
    } else if (actionName === Events.launchreadonly.name) {
      this.auditService.saveAuditAction(
        this.auditService.getActionCode().Read,
        this.auditService.getActionType().Launch,
        this.auditService.getEventOutcome().Success,
        auditData
      );
    } else if (actionName === Events.read.name) {
      log.info(
        'Diagnostic Hub remote action read success for Requested ProcedureID ' +
          workItem.id +
          ' and accession ' +
          workItem.keys.procedureAccessionNo
      );
    } else if (actionName === Events.launchDictation.name) {
      log.info(
        'Diagnostic Hub local action launchDictation  with for Requested ProcedureID ' +
          workItem.id +
          ' and accession ' +
          workItem.keys.procedureAccessionNo
      );
      this.auditService.saveAuditAction(
        this.auditService.getActionCode().Read,
        this.auditService.getActionType().Launch,
        this.auditService.getEventOutcome().Success,
        auditData
      );
    } else if (actionName === Events.externalAccessTab.name) {
    } else if (actionName === Events.takeControl.name) {
      log.info('takeControl action executed successfully for Requested Procedure ID: ' + workItem.id);
    } else if (actionName === Events.previewReport.name) {
      log.info('Exam ' + workItem.id + ' succeeded to launch preview report mode.');
    } else if (actionName === Events.closediscard.name) {
      const workItems = eventMessage.workItem;
      if (workItems.length > 1) {
        const unAssignList = [];
        eventMessage.workItem.forEach(workItem => {
          if (!UtilService.isRemoteExam(workItem)) {
            this.filterReadWorkItem(workItem, unAssignList);
          }
        });
        if (unAssignList.length > 1) {
          if (unAssignList.length === 1) {
            this.doCallUnassign(unAssignList[0]);
          } else {
            this.doCallUnassign(unAssignList);
          }
        }
      } else {
        if (!UtilService.isRemoteExam(workItem)) {
          log.info('releaseLock action executed successfully for Requested Procedure ID: ' + workItem.id);
          // ???? is this executing
          const state = workItem?.state || workItem?.keys?.state;
          //Don't unassign NAW exams
          if (
            !state?.nawState &&
            workItem.associationList &&
            workItem.associationList.length > 0 &&
            eventMessage.params &&
            !eventMessage.params.unassignFlag
          ) {
            const associationList = workItem.associationList;
            workItem = new EventWorkItem(-1);
            workItem.associationList = associationList;
            eventMessage.params.unassignFlag = true;
          }
          if ((eventMessage.params && eventMessage.params.unassignFlag) || workItems[0].isRelease) {
            this.doCallUnassign({ workItem: workItem });
          }
        }
      }
    } else if (actionName === Events.close.name) {
      log.info('close action executed successfully for Requested Procedure ID: ' + workItem.id);
    } else if (actionName === Events.clear.name) {
      log.info('clear action executed successfully for Requested Procedure ID: ' + workItem.id);
    } else if (actionName === Events.closeviewer.name) {
      log.info('closeviewer action executed successfully for Requested Procedure ID: ' + workItem.id);
    } else if (actionName === Events.releaseLock.name) {
      log.info('releaseLock action executed successfully for Requested Procedure ID: ' + workItem.id);
    } else if (actionName === Events.closeExternalTab.name) {
    } else if (actionName === Events.edit.name) {
      log.info('RMS edit action executed successfully for Requested Procedure ID: ' + workItem.id);
    } else if (actionName === Events.editClose.name) {
      log.info('RMS editClose action executed successfully for Requested Procedure ID: ' + workItem.id);
    }
  }

  private auditNawActionForSecondary(auditData, actionType, eventMessage) {
    eventMessage?.params?.addedAssociationList &&
      eventMessage?.params?.addedAssociationList.forEach(assocInfo => {
        const secondaryAuditData = this.createSecondaryAuditData(auditData, assocInfo);
        this.auditNawAction(secondaryAuditData, actionType);
      });

    eventMessage?.params?.removedAssociationList &&
      eventMessage?.params?.removedAssociationList.forEach(assocInfo => {
        const secondaryAuditData = this.createSecondaryAuditData(auditData, assocInfo);
        secondaryAuditData.comment = 'Co-reader value has been cleaned';

        this.auditService.saveAuditAction(
          this.auditService.getActionCode().Delete,
          this.auditService.getActionType().NativeAcademicWorkflowClean,
          this.auditService.getEventOutcome().Success,
          secondaryAuditData
        );
      });
  }

  private createSecondaryAuditData(auditData, assocInfo) {
    const secondaryAuditData = lodash.cloneDeep(auditData);
    const examPart = secondaryAuditData.participants.find(participant => participant.type === 'exam');
    examPart.value = assocInfo.examId;
    examPart.text = assocInfo.accessionNumber;
    return secondaryAuditData;
  }

  private auditNawAction(auditData, actionType) {
    this.auditService.saveAuditAction(
      this.auditService.getActionCode().Create,
      actionType,
      this.auditService.getEventOutcome().Success,
      lodash.cloneDeep(auditData)
    );
    this.auditService.saveAuditAction(
      this.auditService.getActionCode().Create,
      this.auditService.getActionType().Assign,
      this.auditService.getEventOutcome().Success,
      lodash.cloneDeep(auditData)
    );
  }

  private postErrorEventActions(eventMessage: EventMessage, error) {
    const actionName = eventMessage.event.name;
    const workItem = eventMessage.workItem[0];
    const auditData = !!eventMessage.params && eventMessage.params.examAudit;
    this.mergeComments(auditData, workItem, actionName);

    if (actionName === Events.assign.name) {
      this.auditService.saveAuditAction(
        this.auditService.getActionCode().Create,
        this.auditService.getActionType().Assign,
        this.auditService.getEventOutcome().MinorFailure,
        auditData
      );
    } else if (actionName === Events.unassign.name) {
      this.auditService.saveAuditAction(
        this.auditService.getActionCode().Delete,
        this.auditService.getActionType().UnAssign,
        this.auditService.getEventOutcome().MinorFailure,
        auditData
      );
    } else if (actionName === Events.unassignAll.name) {
      log.error('unassignAll action failed for Requested Procedure ID(s), Error: ' + JSON.stringify(error));
    } else if (actionName === Events.assignAll.name) {
      this.auditService.saveAuditAction(
        this.auditService.getActionCode().Update,
        this.auditService.getActionType().Assign,
        this.auditService.getEventOutcome().MinorFailure,
        auditData
      );
    } else if (actionName === Events.selectCoReader.name) {
      this.auditService.saveAuditAction(
        this.auditService.getActionCode().Create,
        this.auditService.getActionType().NativeAcademicSelectCoReader,
        this.auditService.getEventOutcome().MinorFailure,
        auditData
      );
    } else if (actionName === Events.rejectReport.name) {
      this.auditService.saveAuditAction(
        this.auditService.getActionCode().Create,
        this.auditService.getActionType().NativeAcademicWorkflowReject,
        this.auditService.getEventOutcome().MinorFailure,
        auditData
      );
    } else if (actionName === Events.preview.name) {
      this.auditService.saveAuditAction(
        this.auditService.getActionCode().Read,
        this.auditService.getActionType().Preview,
        this.auditService.getEventOutcome().MinorFailure,
        auditData
      );
    } else if (actionName === Events.launchreadonly.name) {
      this.auditService.saveAuditAction(
        this.auditService.getActionCode().Read,
        this.auditService.getActionType().Launch,
        this.auditService.getEventOutcome().MinorFailure,
        auditData
      );
    } else if (actionName === Events.read.name) {
      log.error('Unable to execute read action');
    } else if (actionName === Events.launchDictation.name) {
      log.error(
        'Diagnostic Hub local action launchDictation failed with message  ' +
          JSON.stringify(error) +
          ' for Requested ProcedureID ' +
          workItem.id +
          'and accession ' +
          workItem.keys.procedureAccessionNo
      );
      this.auditService.saveAuditAction(
        this.auditService.getActionCode().Read,
        this.auditService.getActionType().Launch,
        this.auditService.getEventOutcome().MinorFailure,
        auditData
      );
    } else if (actionName === Events.externalAccessTab.name) {
    } else if (actionName === Events.takeControl.name) {
    } else if (actionName === Events.previewReport.name) {
      log.error('Exam ' + workItem.id + ' failed to launch read-only mode with message: ' + JSON.stringify(error));
      log.error(
        'Exam ' + workItem.id + ' failed to launch preview report mode ' + 'with message: ' + JSON.stringify(error)
      );
    } else if (actionName === Events.closediscard.name) {
      eventMessage.workItem.forEach(workItem => {
        this.handleCloseDiscardError(workItem, error);
      });
    } else if (actionName === Events.close.name) {
      log.error('Requested Procedure ID: ' + workItem.id + ', close action failed, Error - ' + JSON.stringify(error));
    } else if (actionName === Events.clear.name) {
      log.error('Requested Procedure ID: ' + workItem.id + ', clear action failed, Error - ' + JSON.stringify(error));
    } else if (actionName === Events.closeviewer.name) {
      log.error(
        'Requested Procedure ID: ' + workItem.id + ', closeViewer action failed, Error - ' + JSON.stringify(error)
      );
    } else if (actionName === Events.releaseLock.name) {
      log.error(
        'Requested Procedure ID: ' +
          workItem.id +
          ', removing read lock failed for the exam, Error - ' +
          JSON.stringify(error)
      );
    } else if (actionName === Events.closeExternalTab.name) {
    }
  }

  getWorkItem(data: any, actionName) {
    if (data) {
      if (data instanceof EventWorkItem) {
        return data;
      }

      const workItem = data.workItem;
      if (workItem) {
        if (workItem instanceof EventWorkItem) {
          return workItem;
        } else {
          return new EventWorkItem(workItem.id, workItem.keys);
        }
      }
      if (data.id) {
        if (data.params) {
          data.params.id = data.id;
        } else {
          data.params = {
            id: data.id
          };
        }
        return new EventWorkItem(data.id, data.params);
      }
      log.error('Requested procedure Id is invalid, Unable to execute ' + actionName + ' action.');
    }
  }

  filterReadWorkItem(workItem, unAssignList) {
    if (workItem.associationList && workItem.associationList.length > 0 && !workItem.isRelease) {
      //Don't unassign NAW exams
      const state = workItem?.state || workItem?.keys?.state;
      if (state && state.nawState) {
        return;
      }

      const associationList = workItem.associationList;
      workItem = new EventWorkItem(-1, Events.closediscard.name);
      workItem.associationList = associationList;
      workItem.isRelease = true;
      unAssignList.push({
        workItem: workItem
      });
    } else if (workItem.isRelease) {
      unAssignList.push({
        workItem: workItem
      });
    }
  }

  doCallUnassign(workItem) {
    const examAudit = this.auditService.getExamAuditInfo(workItem);
    log.debug('audit Info : ' + examAudit);
    examAudit.actor = this.auditService.getActorOnExamRelease('unassign-on-tab-closure');
    log.debug('the actor of the unassign event is : ' + examAudit.actor);
    // unassign(workItem, examAudit);
    this.sendEvent(new EventMessage(Events.unassign, workItem, { examAudit: examAudit }));
  }

  // To prevent viewer launch in the background when the archive adopted popup shows.
  disableAutoLaunchViewerForArchiveAdopted(workItem: EventWorkItem | EventWorkItem[]) {
    if (workItem && workItem.keys.archiveAdoptedStatus === appConstants.archiveAdoptionStatus.ArchiveAdopted) {
      workItem.keys.disableAutoLaunchViewer = true;
    }
  }

  private updateExternalLockInsert(workItem: EventWorkItem): EventWorkItem {
    workItem.externalLockInsert = false;
    if (!!workItem.keys.imageCount && workItem.keys.imageCount !== 0 && !workItem.keys.disableAutoLaunchViewer) {
      workItem.externalLockInsert = true;
    }
    return workItem;
  }

  private beforeEvents() {
    _.each(
      [
        'close',
        'closediscard',
        'clear',
        'closeviewer',
        'autoAdvanceSkipAssign',
        'autoAdvanceSkipUnAssign',
        'closeExternalTab'
      ],
      actionName => {
        this.beforeEvent(actionName, (lifecycle, targets) => {
          this.currentExamContextService.clearContext(lifecycle, targets);
        });
      }
    );
  }

  ngOnDestroy(): void {
    this.clearEventSubscriptions();
  }

  clearEventSubscriptions() {
    this.beforeEventHandlers &&
      this.beforeEventHandlers.forEach((value: ((e: any, t) => void)[]) => {
        if (value && value.length) {
          value.length = 0;
        }
      });

    this.afterSubscriptions &&
      this.afterSubscriptions.forEach(subscriber => {
        subscriber && subscriber.unsubscribe();
      });
  }
}
